// Псевдоним — написание записи в контексте (005: FR-005, FR-006, FR-010а; ADR-0024, research, Р7).
// Рабочий — подтверждённый человеком или точное совпадение после нормализации; предложенный
// связывает только с вопросом, отклонённый не предлагается снова без нового сильного признака.
import { z } from '../kernel/zod.ts';
import { IdSchema, PersonRefSchema, TimestampSchema } from '../kernel/common.ts';

/** Контекст псевдонима; подразделение зоны документа — 006 (FR-017). */
export const aliasScopeKinds = [
  'everywhere',
  'legal_entity',
  'unit',
  'document',
  'section',
] as const;
export type AliasScopeKind = (typeof aliasScopeKinds)[number];
export const AliasScopeKindSchema = z.enum(aliasScopeKinds);

export const aliasScopeKindLabels = {
  everywhere: 'Везде',
  legal_entity: 'Юрлицо',
  unit: 'Подразделение',
  document: 'Документ',
  section: 'Раздел',
} as const satisfies Record<AliasScopeKind, string>;

export const SECTION_MAX = 500;

export const AliasScopeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('everywhere') }),
  z.object({ kind: z.literal('legal_entity'), legalEntityId: IdSchema }),
  z.object({ kind: z.literal('unit'), unitId: IdSchema }),
  z.object({ kind: z.literal('document'), documentId: IdSchema }),
  z.object({
    kind: z.literal('section'),
    documentId: IdSchema,
    section: z.string().min(1).max(SECTION_MAX),
  }),
]);
export type AliasScope = z.infer<typeof AliasScopeSchema>;

export const aliasStates = ['confirmed', 'suggested', 'rejected'] as const;
export type AliasState = (typeof aliasStates)[number];
export const AliasStateSchema = z.enum(aliasStates);

export const aliasStateLabels = {
  confirmed: 'Подтверждён',
  suggested: 'Предложен',
  rejected: 'Не он',
} as const satisfies Record<AliasState, string>;

/** Откуда псевдоним: из упоминания при связывании или добавлен вручную (сразу подтверждён). */
export const aliasSources = ['mention', 'manual'] as const;
export type AliasSource = (typeof aliasSources)[number];
export const AliasSourceSchema = z.enum(aliasSources);

export const ALIAS_TEXT_MAX = 200;

export const AliasSchema = z.object({
  id: IdSchema,
  recordId: IdSchema,
  /** Написание как в первом упоминании или как ввёл человек. */
  text: z.string(),
  /** Ключ сравнения после нормализации (research, Р4). */
  textNorm: z.string(),
  scope: AliasScopeSchema,
  state: AliasStateSchema,
  source: AliasSourceSchema,
  /** Статистика (FR-005): сколько раз, в скольких документах, первое и последнее появление. */
  count: z.number().int().min(0),
  documents: z.number().int().min(0),
  firstSeen: TimestampSchema.nullable(),
  lastSeen: TimestampSchema.nullable(),
  confirmedBy: PersonRefSchema.nullable(),
  confirmedAt: TimestampSchema.nullable(),
});
export type Alias = z.infer<typeof AliasSchema>;

/**
 * «Не он» и его «Отменить» (FR-006; ASG-RES-13, правка 0.22.5): псевдоним после действия и сколько
 * упоминаний изменилось — ушли на разбор или связаны снова.
 */
export const AliasRejectionSchema = z.object({
  alias: AliasSchema,
  mentions: z.number().int().min(0),
});
export type AliasRejection = z.infer<typeof AliasRejectionSchema>;
